import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type ModelInvocationLog } from "@/lib/forex-api";

const statusConfig: Record<string, { cls: string; label: string }> = {
  success: { cls: "bg-emerald-500/10 text-emerald-500", label: "成功" },
  fallback: { cls: "bg-amber-500/10 text-amber-500", label: "降级" },
  error: { cls: "bg-red-500/10 text-red-500", label: "错误" },
};

function formatTime(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

export default function ModelLogsDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logId = searchParams.get("id") || "";
  const [log, setLog] = useState<ModelInvocationLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    forexApi
      .listModelInvocationLogs()
      .then((result) => {
        if (cancelled) return;
        const matched = result.items.find((item) => item.log_id === logId) || null;
        setLog(matched);
        if (!matched) setError("未找到模型调用日志，请从日志列表重新进入。");
      })
      .catch(() => {
        if (!cancelled) setError("模型调用日志接口不可用，请稍后重试。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [logId]);

  const st = log ? statusConfig[log.status] || { cls: "bg-surface text-text-secondary", label: log.status } : null;

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "智能体运营", href: "/forex/model-logs" }, { label: "模型调用日志详情" }]} />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">模型调用日志详情</h2>
          <p className="text-xs text-text-muted mt-0.5">{log ? `${log.agent_name} · ${log.log_id}` : logId || "未选择日志"}</p>
        </div>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line text-sm" />返回日志列表
        </button>
      </div>

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && log && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-surface-border p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold text-text-primary mb-3">请求上下文</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: "日志ID", value: log.log_id, mono: true },
                  { label: "时间", value: formatTime(log.created_at), mono: true },
                  { label: "智能体", value: log.agent_name },
                  { label: "智能体ID", value: log.agent_id, mono: true },
                  { label: "AI 角色", value: log.role_key, mono: true },
                  { label: "Provider", value: log.provider },
                  { label: "Model", value: log.model_id, mono: true },
                  { label: "请求类型", value: log.request_type, mono: true },
                  { label: "状态", value: st?.label || log.status },
                ].map((row) => (
                  <div key={row.label} className="bg-surface/50 rounded-md p-2.5">
                    <span className="text-[10px] text-text-muted font-medium uppercase">{row.label}</span>
                    <p className={`text-xs text-text-primary mt-0.5 ${row.mono ? "font-mono" : ""}`}>{row.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">模型响应摘要</h3>
              <div className="space-y-2.5">
                <div className="bg-surface/50 rounded-md p-3 text-center">
                  <p className="text-[11px] text-text-muted">状态</p>
                  <p className={`text-sm font-semibold mt-1 ${st?.cls}`}>{st?.label}</p>
                </div>
                <div className="bg-surface/50 rounded-md p-3 text-center">
                  <p className="text-[11px] text-text-muted">延迟</p>
                  <p className={`text-lg font-semibold font-mono mt-1 ${log.latency_ms > 120 ? "text-red-500" : log.latency_ms > 60 ? "text-amber-500" : log.latency_ms > 0 ? "text-emerald-500" : "text-text-muted"}`}>
                    {log.latency_ms > 0 ? `${log.latency_ms}ms` : "--"}
                  </p>
                </div>
                <div className="bg-surface/50 rounded-md p-3">
                  <p className="text-[11px] text-text-muted mb-2">Token 使用</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs"><span className="text-text-muted">Prompt</span><span className="text-text-primary font-mono">{log.prompt_tokens.toLocaleString()}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-text-muted">Completion</span><span className="text-text-primary font-mono">{log.completion_tokens.toLocaleString()}</span></div>
                    <div className="flex justify-between text-xs font-semibold pt-1 border-t border-surface-border"><span className="text-text-muted">总计</span><span className="text-text-primary font-mono">{log.total_tokens.toLocaleString()}</span></div>
                  </div>
                </div>
                <div className="bg-surface/50 rounded-md p-3 text-center">
                  <p className="text-[11px] text-text-muted">成本</p>
                  <p className="text-lg font-semibold font-mono text-text-primary mt-1">{log.cost.toFixed(6)}</p>
                </div>
              </div>
            </div>
          </div>

          {log.error_message && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-red-500 mb-2">错误信息</h3>
              <p className="text-xs text-red-500 font-mono">{log.error_message}</p>
            </div>
          )}

          {log.fallback_model_id && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-500 mb-2">降级信息</h3>
              <p className="text-xs text-amber-500">主模型调用失败或超时，降级候选模型 <span className="font-mono font-medium">{log.fallback_model_id}</span></p>
            </div>
          )}

          <div className="bg-white rounded-lg border border-surface-border p-4">
            <h3 className="text-sm font-semibold text-text-primary mb-3">调用质量</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { label: "响应时间", value: log.latency_ms > 200 || log.latency_ms === 0 ? "异常" : "正常", pass: log.latency_ms > 0 && log.latency_ms <= 200 },
                { label: "调用状态", value: st?.label || log.status, pass: log.status === "success" },
                { label: "Token 使用", value: log.total_tokens > 4000 ? "偏高" : "正常", pass: log.total_tokens <= 4000 },
              ].map((item) => (
                <div key={item.label} className="bg-surface/50 rounded-md p-3 flex items-center gap-2.5">
                  <i className={`text-base ${item.pass ? "ri-checkbox-circle-fill text-emerald-500" : "ri-close-circle-fill text-red-500"}`} />
                  <div>
                    <span className="text-[11px] text-text-muted">{item.label}</span>
                    <p className={`text-xs font-medium ${item.pass ? "text-emerald-500" : "text-red-500"}`}>{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
